import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SimpleTable from "../../../../../../components/SimpleTable";
import { currencyFormatter } from "../../../../../../utils/helpers/currencyFormatter";
import { renderDurationAsFormat } from "../../../../../../utils/helpers/date";
import { getHourTypeColor } from "../../../../../../utils/helpers/hourType";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import {
	useCompanyCurrency,
	useCompanyTimeFormat,
} from "../../../../../../utils/hooks/company";

const columnHelper = createColumnHelper();

const Table = ({ data, week, canViewRates }) => {
	const { t } = useTranslation();
	const timeFormat = useCompanyTimeFormat();
	const currency = useCompanyCurrency();
	const hideRetroAdjustment = usePreferences("timeSheetGeneral.hideRetroAdjustment");

	const sortedData = useMemo(() => [...(data.sort((a, b) => {
		if (a.total === b.total) {
			return b.duration - a.duration;
		}
		else {
			return b.total - a.total;
		}
	})), ...(((week.retroHours || week.retroAmount) && !hideRetroAdjustment) ? [
		{
			payCode: {
				code: "Retro",
				hourType: "retro",
			},
			duration: week.retroHours || 0,
			total: week.retroAmount || 0,
		}
	] : [])], [data, week, hideRetroAdjustment]);

	const columns = useMemo(
		() => [
			columnHelper.accessor("payCode", {
				header: t("code"),
				cell: (info) => {
					const value = info.getValue();
					return (
						<div className="font-weight-bold" style={{
							backgroundColor: getHourTypeColor(value.hourType),
							color:
								value?.hourType === "unpaid" ? "#00080e" : "white",
							borderBottom: "1px solid white",
							padding: "2px 5px",
							borderRadius: "7px",
							width: "60px",
							textAlign: "center",
						}}>
							{value.code}
						</div>
					);
				},
				footer: <div className="font-weight-bold" style={{
					backgroundColor: getHourTypeColor("regular"),
					color: "white",
					borderBottom: "1px solid white",
					padding: "2px 5px",
					borderRadius: "7px",
					width: "60px",
					textAlign: "center",
				}}>{t("total")}</div>,
			}),
			columnHelper.accessor("duration", {
				header: t("hours"),
				cell: (info) => {
					const value = info.getValue() || info.row.original.otherDuration;
					if (value === undefined) {
						return null;
					}

					return (
						<span>
							{renderDurationAsFormat(value, timeFormat, false, true)}
						</span>
					);
				},
				footer: ({ table }) => {
					const rows = table.getCoreRowModel().rows;
					const value = rows.reduce((total, row) => {
						return total + (row.original.duration || row.original.otherDuration);
					}, 0);
					return (
						<span className="font-weight-bold">
							{renderDurationAsFormat(value, timeFormat, false, true)}
						</span>
					);
				},
			}),
			...(canViewRates ? [
				columnHelper.accessor("total", {
					header: t("total"),
					cell: (info) => {
						const value = info.getValue();
						if (value === undefined) {
							return null;
						}
						return (
							<span>
								{currencyFormatter(value || 0, 2, currency)}
							</span>
						);
					},
					footer: ({ table }) => {
						const rows = table.getCoreRowModel().rows;
						const value = rows.reduce((total, row) => {
							return total + row.original.total;
						}, 0);
						return (
							<span className="font-weight-bold">
								{currencyFormatter(value, 2, currency)}
							</span>
						);
					},
				}),
			] : []),
		],
		[t, canViewRates, timeFormat, currency],
	);

	return <SimpleTable columns={columns} data={sortedData} />;
};

export default Table;
